import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Clock, Briefcase } from 'lucide-react';
type RoleCount = {
  role: string;
  count: number;
};
type SubmissionStats = {
  totalSubmissions: number;
  totalExperience: number;
  roleCounts: RoleCount[];
};
const formatRole = (role: string) => {
  return role.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};
const SubmissionStats = () => {
  const fetchStats = async (): Promise<SubmissionStats> => {
    console.log("Fetching stats...");

    // Get total submissions without any status filter
    const {
      count: totalSubmissions,
      error: countError
    } = await supabase.from('quick_submissions').select('*', {
      count: 'exact',
      head: true
    });
    if (countError) {
      console.error("Error fetching total submissions:", countError);
      throw countError;
    }
    console.log("Total submissions:", totalSubmissions);

    // Get role counts without any status filter
    const {
      data: roleData,
      error: roleError
    } = await supabase.from('quick_submissions').select('role');
    if (roleError) {
      console.error("Error fetching roles:", roleError);
      throw roleError;
    }
    console.log("Role data:", roleData);

    // Filter out null roles and count occurrences
    const roleCountMap: Record<string, number> = {};
    roleData.forEach(item => {
      if (item.role) {
        const role = item.role;
        roleCountMap[role] = (roleCountMap[role] || 0) + 1;
      }
    });

    // Convert to array of objects
    const roleCountArray = Object.entries(roleCountMap).map(([role, count]) => ({
      role,
      count
    }));
    console.log("Role counts:", roleCountArray);

    // Get total years of experience without any status filter
    const {
      data: experienceData,
      error: experienceError
    } = await supabase.from('quick_submissions').select('total_experience');
    if (experienceError) {
      console.error("Error fetching experience data:", experienceError);
      throw experienceError;
    }
    console.log("Experience data:", experienceData);

    // Calculate sum of years of experience
    const totalExperience = experienceData.reduce((sum, item) => {
      return sum + (item.total_experience || 0);
    }, 0);
    console.log("Total experience:", totalExperience);
    return {
      totalSubmissions: totalSubmissions || 0,
      totalExperience,
      roleCounts: roleCountArray
    };
  };
  const {
    data: stats,
    isLoading,
    error
  } = useQuery({
    queryKey: ['submission-stats'],
    queryFn: fetchStats,
    refetchInterval: 100000000 // Refetch every 10 seconds to keep stats updated
  });
  if (isLoading) {
    return <div className="py-8">
        <p className="text-center text-gray-500">Loading statistics...</p>
      </div>;
  }
  if (error || !stats) {
    console.error("Error in stats component:", error);
    return <div className="py-8">
        <p className="text-center text-gray-500">Failed to load statistics</p>
      </div>;
  }
  return <div className="py-16 bg-gradient-to-br from-white via-[#F0F9FF] to-[#E0F2FE]">
      <div className="container mx-auto px-4 hidden">
        <h2 className="text-3xl font-bold text-center mb-12 bg-clip-text text-transparent bg-gradient-to-r from-[#0EA5E9] to-[#6366F1]">Talento AI Community Stats</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="bg-white border border-[#0EA5E9]/20 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center text-xl text-gray-800">
                <Users className="h-5 w-5 mr-2 text-[#0EA5E9]" />
                Total Profiles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-[#0EA5E9]">{stats.totalSubmissions}</p>
              <p className="text-sm text-gray-500">Talented professionals</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white border border-[#0EA5E9]/20 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center text-xl text-gray-800">
                <Clock className="h-5 w-5 mr-2 text-[#0EA5E9]" />
                Combined Experience
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-[#0EA5E9]">{stats.totalExperience}+</p>
              <p className="text-sm text-gray-500">Years of expertise</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white border border-[#0EA5E9]/20 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center text-xl text-gray-800">
                <Briefcase className="h-5 w-5 mr-2 text-[#0EA5E9]" />
                Top Roles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-[#0EA5E9]">{stats.roleCounts.length}</p>
              <p className="text-sm text-gray-500">Diverse skill sets</p>
            </CardContent>
          </Card>
        </div>
        
        <div className="bg-white border border-[#0EA5E9]/20 shadow-lg rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4 text-gray-800">Role Distribution</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {stats.roleCounts.sort((a, b) => b.count - a.count).slice(0, 12).map((roleCount, index) => <div key={index} className="bg-gray-50 p-3 rounded border border-gray-100">
                <p className="font-medium text-gray-800">{formatRole(roleCount.role)}</p>
                <p className="text-sm text-gray-500">{roleCount.count} profiles</p>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
};
export default SubmissionStats;